import { AutoComplete } from "@arco-design/web-react";
import { History, X } from "lucide-react";
import { useCallback, useMemo, useState, type MouseEvent, type ReactNode } from "react";

import {
  clearSearchHistory,
  readSearchHistory,
  rememberSearchHistory,
  removeSearchHistory,
} from "../../lib/searchHistoryStorage";
import "./SearchHistoryInput.css";

type SearchHistoryInputProps = {
  storageKey: string;
  value?: string;
  placeholder?: string;
  allowClear?: boolean;
  className?: string;
  disabled?: boolean;
  "data-testid"?: string;
  onChange?: (value: string) => void;
  onPressEnter?: () => void;
};

/** 阻止 Autocomplete 在点击删除/清空时选中项或失焦关闭。 */
function stopPopupControlEvent(event: MouseEvent) {
  event.preventDefault();
  event.stopPropagation();
}

/**
 * 带浏览器本地搜索历史的输入框。
 * 有历史时聚焦下拉展示最近最多 5 条（不按当前输入过滤）；无历史不展示候选。
 */
export function SearchHistoryInput({
  storageKey,
  value,
  placeholder,
  allowClear = true,
  className,
  disabled,
  "data-testid": dataTestId,
  onChange,
  onPressEnter,
}: SearchHistoryInputProps) {
  const [history, setHistory] = useState(() => readSearchHistory(storageKey));

  const refreshHistory = useCallback(() => {
    setHistory(readSearchHistory(storageKey));
  }, [storageKey]);

  const remember = useCallback(
    (raw: string) => {
      setHistory(rememberSearchHistory(storageKey, raw));
    },
    [storageKey],
  );

  const handleRemove = useCallback(
    (entry: string) => {
      setHistory(removeSearchHistory(storageKey, entry));
    },
    [storageKey],
  );

  const handleClearAll = useCallback(() => {
    setHistory(clearSearchHistory(storageKey));
  }, [storageKey]);

  const historyOptions = useMemo(() => {
    if (history.length === 0) {
      return undefined;
    }
    return history.map((entry) => ({
      value: entry,
      name: (
        <div className="fm-search-history-option">
          <History size={14} className="fm-search-history-option__icon" aria-hidden />
          <span className="fm-search-history-option__text">{entry}</span>
          <span
            className="fm-search-history-option__remove"
            role="button"
            tabIndex={-1}
            aria-label={`删除搜索记录 ${entry}`}
            data-testid="search-history-remove"
            onMouseDown={stopPopupControlEvent}
            onClick={(event) => {
              stopPopupControlEvent(event);
              handleRemove(entry);
            }}
          >
            <X size={12} aria-hidden />
          </span>
        </div>
      ),
    }));
  }, [handleRemove, history]);

  const dropdownRender = useCallback(
    (menu: ReactNode) => (
      <div className="fm-search-history-popup" data-testid="search-history-popup">
        <div className="fm-search-history-popup__header">最近搜索</div>
        <div className="fm-search-history-popup__list">{menu}</div>
        <button
          type="button"
          className="fm-search-history-popup__clear"
          data-testid="search-history-clear"
          onMouseDown={stopPopupControlEvent}
          onClick={(event) => {
            stopPopupControlEvent(event);
            handleClearAll();
          }}
        >
          清空记录
        </button>
      </div>
    ),
    [handleClearAll],
  );

  return (
    <AutoComplete
      className={["fm-search-history-input", className].filter(Boolean).join(" ")}
      allowClear={allowClear}
      disabled={disabled}
      placeholder={placeholder}
      data={historyOptions}
      value={value ?? ""}
      data-testid={dataTestId}
      filterOption={false}
      defaultActiveFirstOption={false}
      getPopupContainer={() => document.body}
      triggerProps={{ autoAlignPopupWidth: true, position: "bl" }}
      dropdownRender={history.length > 0 ? dropdownRender : undefined}
      onFocus={refreshHistory}
      onChange={(next) => onChange?.(next)}
      onSelect={(next) => {
        onChange?.(next);
        remember(next);
      }}
      onPressEnter={() => {
        remember(value ?? "");
        onPressEnter?.();
      }}
    />
  );
}
